/**
 * MediaDatePicker — the Media Centre start/end date-range popup.
 *
 * Extracted from MediaCentre.js. The two calendars are identical apart from
 * start/end wiring, so the custom header (year/month dropdowns with
 * availability gating) is defined once here and reused, rather than duplicated.
 * Behaviour is intentionally identical to the original inline block.
 */
import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { monthNames, formatDate } from './helpers';

function MediaDatePicker({
    tempStartDate,
    tempEndDate,
    setTempStartDate,
    setTempEndDate,
    availableYearsAndMonths,
    onApply,
    onClear,
    onCancel,
}) {
    // Custom year/month header, gated to the years/months that actually have
    // content and never beyond the current month. Shared by both calendars.
    const renderCustomHeader = ({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();

        const availableYears = [...availableYearsAndMonths.keys()]
            .sort((a, b) => b - a)
            .filter((year) => year <= currentYear);

        const availableMonths = availableYearsAndMonths.get(date.getFullYear()) || new Set();

        const shouldDisableMonth = (monthIndex) => {
            const yearToCheck = date.getFullYear();
            if (yearToCheck > currentYear) return true;
            if (yearToCheck === currentYear && monthIndex > currentMonth) return true;
            return !availableMonths.has(monthIndex);
        };

        return (
            <div className="custom-header">
                <button onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
                    {'<'}
                </button>
                <select
                    value={date.getFullYear()}
                    onChange={({ target: { value } }) => {
                        const newYear = parseInt(value);
                        if (newYear <= currentYear) {
                            changeYear(newYear);
                        }
                    }}
                >
                    {availableYears.map((year) => (
                        <option key={year} value={year} disabled={year > currentYear}>
                            {year}
                        </option>
                    ))}
                </select>

                <select
                    value={date.getMonth()}
                    onChange={({ target: { value } }) => {
                        const newMonth = parseInt(value);
                        if (!shouldDisableMonth(newMonth)) {
                            changeMonth(newMonth);
                        }
                    }}
                >
                    {monthNames.map((option, i) => (
                        <option key={i} value={i} disabled={shouldDisableMonth(i)}>
                            {option}
                        </option>
                    ))}
                </select>

                <button
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled || shouldDisableMonth(date.getMonth() + 1)}
                >
                    {'>'}
                </button>
            </div>
        );
    };

    return (
        <div className="date-range-filter" style={{ position: 'relative', display: 'inline-block' }}>
            <div className="date-picker-overlay" onClick={onCancel}></div>
            <div className="date-picker-popup">
                <div className="date-picker-calendars">
                    <div className="calendar-container">
                        <div className="calendar-header">Start Date</div>
                        <DatePicker
                            selected={tempStartDate}
                            onChange={(date) => setTempStartDate(date)}
                            selectsStart
                            startDate={tempStartDate}
                            endDate={tempEndDate}
                            maxDate={new Date()}
                            inline
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            dateFormat="dd/MM/yyyy"
                            calendarClassName="custom-calendar"
                            renderCustomHeader={renderCustomHeader}
                        />
                        <div className="calendar-footer">
                            {tempStartDate ? formatDate(tempStartDate) : 'No date selected'}
                        </div>
                    </div>
                    <div className="calendar-container">
                        <div className="calendar-header">End Date</div>
                        <DatePicker
                            selected={tempEndDate}
                            onChange={(date) => setTempEndDate(date)}
                            selectsEnd
                            startDate={tempStartDate}
                            endDate={tempEndDate}
                            minDate={tempStartDate}
                            maxDate={new Date()}
                            inline
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            dateFormat="dd/MM/yyyy"
                            calendarClassName="custom-calendar"
                            renderCustomHeader={renderCustomHeader}
                        />
                        <div className="calendar-footer">
                            {tempEndDate ? formatDate(tempEndDate) : 'No date selected'}
                        </div>
                    </div>
                </div>
                <div className="date-picker-actions">
                    {(tempStartDate || tempEndDate) && (
                        <button className="date-picker-clear" onClick={onClear}>
                            Clear
                        </button>
                    )}
                    <div className="date-picker-actions-right">
                        <button className="date-picker-cancel" onClick={onCancel}>
                            Cancel
                        </button>
                        <button
                            className="date-picker-apply"
                            onClick={onApply}
                            disabled={!tempStartDate || !tempEndDate}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MediaDatePicker;
